// @flow

import React from "react";
import Modal from "react-modal";
import "./Footer.scss";
import type {translate} from "../../i18next";
import {Trans} from "react-i18next";
import {emitInternalEvent} from "../../helper/tracker/internalEvents";
import WarrantyNoticeModal from "../empco/WarrantyNoticeModal";

const modalStyles = {
    overlay: {
        zIndex: "100000"
    },
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        border: "1px solid grey",
        width: "calc(90%)",
        maxWidth: "1000px",
        minHeight: "200px",
        maxHeight: "90vh",
        overflowY: "auto",
    }
};

const downloadButtonStyles = {
    display: "flex",
    justifyContent: "end",
    marginRight: "3em"
};

export type FooterProps = {
    t: translate,
    showFooter: boolean,
    legalTerms: string,
    revocationTerms: string,
    revocationLink: string,
    paymentTerms: string,
    privacyPolicy: string,
    privacyPolicyLink: string,
    imprintText: string,
    imprintLink: string,
    dealerId: string,
    cssBaseUrl: string,
    canShowOnlineRevocation: boolean,
    euShop: boolean,
    notificationUrl: string
}
export type FooterState = {
    showPrivacyPolicy: boolean,
    showLegalTerms: boolean,
    showRevocationTerms: boolean,
    showPaymentTerms: boolean,
    showImprintText: boolean,
    showWarrantyNotice: boolean,
}
export const initialState = {
    showLegalTerms: false,
    showRevocationTerms: false,
    showPaymentTerms: false,
    showPrivacyPolicy: false,
    showImprintText: false,
    showWarrantyNotice: false,
};

class Footer extends React.Component<FooterProps, FooterState> {
    constructor(props) {
        super(props);
        this.state = initialState;
    }

    toggleAGB = () => {
        if (!this.state.showLegalTerms) emitInternalEvent("text_terms_view");
        this.setState({
            ...initialState,
            showLegalTerms: !this.state.showLegalTerms
        });
    };
    togglePrivacyPolicy = () => {
        if (!this.state.showPrivacyPolicy) emitInternalEvent("text_privacy_view");
        this.setState({
            ...initialState,
            showPrivacyPolicy: !this.state.showPrivacyPolicy
        });
    };
    toggleReturnAgreement = () => {
        if (!this.state.showRevocationTerms) emitInternalEvent("text_revocation_view");
        this.setState({
            ...initialState,
            showRevocationTerms: !this.state.showRevocationTerms
        });
    };
    togglePayment = () => {
        this.setState({
            ...initialState,
            showPaymentTerms: !this.state.showPaymentTerms
        });
    };
    toggleImprintText = () => {
        if (!this.state.showImprintText) emitInternalEvent("text_imprint_view");
        this.setState({
            ...initialState,
            showImprintText: !this.state.showImprintText
        });
    };

    toggleWarrantyNotice = () => {
        if (!this.state.showWarrantyNotice) emitInternalEvent("text_warranty_view");
        this.setState({
            ...initialState,
            showWarrantyNotice: !this.state.showWarrantyNotice
        });
    };

    emitOnlineRevocation = () => {
        emitInternalEvent("online_revocation");
    };

    revocationPortalUrl() {
        return `${this.props.cssBaseUrl}/revoke?dealer=${encodeURIComponent(btoa(this.props.dealerId))}`;
    }

    // The dealer-maintained revocation link wins over the revocation texts (same pattern as
    // privacy policy / imprint). The online revocation portal stays reachable in every variant.
    renderRevocation() {
        if (this.props.revocationLink) {
            return <React.Fragment>
                <a className={"link"} href={this.props.revocationLink} target={"_blank"} rel={"noopener noreferrer"}
                   onClick={() => emitInternalEvent("text_revocation_view")}>
                    {this.props.t("footer.return")}
                </a>
                {this.props.canShowOnlineRevocation &&
                    <a className="link"
                       href={this.revocationPortalUrl()}
                       onClick={this.emitOnlineRevocation}
                       target={"_blank"} rel={"noopener noreferrer"}>
                        {this.props.t("footer.onlineRevocation")}
                    </a>
                }
            </React.Fragment>
        }
        if (this.props.revocationTerms) {
            return <React.Fragment>
                <button type={"button"} onClick={this.toggleReturnAgreement}>{this.props.t("footer.return")}</button>
                <Modal isOpen={this.state.showRevocationTerms}
                       style={modalStyles}
                       shouldCloseOnOverlayClick={true}
                       onRequestClose={this.toggleReturnAgreement}
                >
                    <button type={"button"} aria-label={this.props.t("footer.closeModal")}
                            onClick={this.toggleReturnAgreement} className="hpm-modalClose"/>
                    {this.props.canShowOnlineRevocation &&
                        <div className="hpm-online-revocation">
                            <span>{this.props.t("footer.onlineRevocationIntro")}</span>
                            <a className="hpm-online-revocation-button"
                               href={this.revocationPortalUrl()}
                               onClick={this.emitOnlineRevocation}
                               target={"_blank"} rel={"noopener noreferrer"}>
                                {this.props.t("footer.revokeContract")}
                            </a>
                        </div>
                    }
                    <div dangerouslySetInnerHTML={{__html: this.props.revocationTerms}}
                         className="hpm-modalContent"
                         style={{fontFamily: "var(--font100)"}}/>
                </Modal>
            </React.Fragment>
        }
        if (this.props.canShowOnlineRevocation) {
            return <a className="link"
                      href={this.revocationPortalUrl()}
                      onClick={this.emitOnlineRevocation}
                      target={"_blank"} rel={"noopener noreferrer"}>
                {this.props.t("footer.onlineRevocation")}
            </a>
        }
        return null;
    }

    // EU harmonised notice on the legal guarantee (#56431) - only for garages selling into the EU
    renderWarrantyNotice() {
        if (!this.props.euShop) {
            return null;
        }
        return <React.Fragment>
            <button type={"button"} onClick={this.toggleWarrantyNotice}>{this.props.t("warranty.notice.link")}</button>
            <WarrantyNoticeModal isOpen={this.state.showWarrantyNotice}
                                 onRequestClose={this.toggleWarrantyNotice}
                                 t={this.props.t}/>
        </React.Fragment>;
    }

    renderPrivacyPolicy() {
        if(this.props.privacyPolicyLink) {
            return <React.Fragment>
                <a className={"link"} href={this.props.privacyPolicyLink} target={"_blank"} rel={"noopener noreferrer"}
                   onClick={() => emitInternalEvent("text_privacy_view")}>
                    {this.props.t("footer.privacyPolicy")}
                </a>
            </React.Fragment>
        }
        if(this.props.privacyPolicy) {
            return <React.Fragment>
                <button
                    type={"button"}
                    onClick={this.togglePrivacyPolicy}>{this.props.t("footer.privacyPolicy")}</button>
                <Modal isOpen={this.state.showPrivacyPolicy}
                       style={modalStyles}
                       shouldCloseOnOverlayClick={true}
                       onRequestClose={this.togglePrivacyPolicy}
                >
                    <button type={"button"} onClick={this.togglePrivacyPolicy} className="hpm-modalClose"/>
                    <div dangerouslySetInnerHTML={{__html: this.props.privacyPolicy}}
                         className="hpm-modalContent"
                         style={{fontFamily: "var(--font100)"}}/>
                </Modal>
            </React.Fragment>
        }
        return null;
    }

    renderImprint() {
        if(this.props.imprintLink) {
            return <React.Fragment>
                <a className={"link"} href={this.props.imprintLink} target={"_blank"} rel={"noopener noreferrer"}
                   onClick={() => emitInternalEvent("text_imprint_view")}>
                    {this.props.t("footer.imprint")}
                </a>
            </React.Fragment>
        }
        if(this.props.imprintText) {
            return  <React.Fragment>
                <button
                    type={"button"}
                    onClick={this.toggleImprintText}>{this.props.t("footer.imprint")}</button>
                <Modal isOpen={this.state.showImprintText}
                       style={modalStyles}
                       shouldCloseOnOverlayClick={true}
                       onRequestClose={this.toggleImprintText}
                >
                    <button type={"button"} onClick={this.toggleImprintText} className="hpm-modalClose"/>
                    <div dangerouslySetInnerHTML={{__html: this.props.imprintText}}
                         className="hpm-modalContent"
                         style={{fontFamily: "var(--font100)"}}/>
                    <br/><br/>
                    <hr/>
                    <br/>
                    <div className="hpm-modalContent" style={{fontFamily: "var(--font100)"}}>
                        <Trans i18nKey="footer.slImprint">
                            <b>Betreiber des Zubehör-Navigators:</b><br/>
                            Dr. Schnell & Dr. Hensel GmbH<br/>
                            Stangenland 2<br/>
                            18146 Rostock<br/>
                            Deutschland<br/><br/>
                            Telefon: +49 (0)381 666 989-100<br/>
                            E-Mail: <a href="mailto:kontakt@it-tecture.de">kontakt@it-tecture.de</a><br/>
                            Geschäftsführung: Dr. Arne Schnell, Dr. Arne Hensel<br/>
                            Amtsgericht Rostock: HRB 9884<br/>
                            USt.-IdNr.: DE 814114196<br/><br/>
                            Alternative Streitbeilegung gemäß Art. 14 Abs. 1 ODR-VO und § 36 VSBG:<br/>
                            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit, die
                            Sie unter <a
                            href="http://ec.europa.eu/consumers/odr/">http://ec.europa.eu/consumers/odr/</a> finden.
                            Zur Teilnahme an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
                            sind wir nicht verpflichtet und grundsätzlich nicht bereit.
                        </Trans>
                    </div>
                </Modal>
            </React.Fragment>
        }
        return null;
    }

    render() {
        if (!this.props.showFooter) return null;
        return <div className="hpm-footer_navigation hpm-noPrint">
            {this.props.legalTerms &&
                <React.Fragment>
                    <button type={"button"} onClick={this.toggleAGB}>{this.props.t("footer.legalterms")}</button>
                    <Modal isOpen={this.state.showLegalTerms}
                           style={modalStyles}
                           shouldCloseOnOverlayClick={true}
                           onRequestClose={this.toggleAGB}
                    >
                        <button type={"button"} aria-label={this.props.t("footer.closeModal")} onClick={this.toggleAGB}
                                className="hpm-modalClose"/>
                        <div style={downloadButtonStyles}>
                            <a className={"file-download-button"} id="file-download-button"
                               download={this.props.t("cart.calculation.legalTerms.fileName")}
                               href={`${this.props.notificationUrl}/download/legalTerms/${this.props.dealerId}`}>{this.props.t("cart.calculation.legalTerms.button")}</a>
                        </div>
                        <div dangerouslySetInnerHTML={{__html: this.props.legalTerms}}
                             className="hpm-modalContent" style={{fontFamily: "var(--font100)"}}/>
                    </Modal>
                </React.Fragment>
            }
            {this.renderRevocation()}
            {this.renderWarrantyNotice()}
            {this.props.paymentTerms && <React.Fragment>
                <button
                    type={"button"}
                    onClick={this.togglePayment}>{this.props.t("footer.paymentAndShipping")}</button>
                <Modal isOpen={this.state.showPaymentTerms}
                       style={modalStyles}
                       shouldCloseOnOverlayClick={true}
                       onRequestClose={this.togglePayment}
                >
                    <button type={"button"} aria-label={this.props.t("footer.closeModal")} onClick={this.togglePayment}
                            className="hpm-modalClose"/>
                    <div dangerouslySetInnerHTML={{__html: this.props.paymentTerms}}
                         className="hpm-modalContent"
                         style={{fontFamily: "var(--font100)"}}/>
                </Modal>
            </React.Fragment>}
            {this.renderPrivacyPolicy()}
            {this.renderImprint()}
        </div>;
    }
}

export default Footer;
